import { Empty, FoldablePanel, Note } from "./Panel";
import { PnLShape } from "./PnLShape";
import { RateCell, RateHead } from "./RateCell";
import { useJournalShapes } from "../hooks/useShapes";
import { fragility, fragilityTone, rateTone, rateWord, shapeOf } from "../lib/shape";
import { useCohortSignals, useCohortSummary } from "../hooks/useCohort";
import type { CohortBucket, CohortSignal, ShadowShape } from "../api/types";
import {
  adverse,
  bucketLabel,
  stepLabel,
  stepUSD,
  pnlTone,
  pnlWord,
  lead,
  outcomeWord,
  confirmTitle,
  takerReasonWord,
  ratio,
  rowTone,
  ruleWord,
  soloShare,
  soloTone,
  usd,
  verdict,
  zTone,
  zWord,
} from "../lib/cohort";

/**
 * Перевес группы «плюсовых».
 *
 * # Зачем панель на экране
 *
 * Панель участников показывает перевес группы с 2026-08-12 и молчит о том,
 * стоит ли за ним идти. Здесь тот же перевес превращён в ставку и сведён по
 * исходу окна — и раздел обязан ответить не «в плюсе ли мы», а тремя парами
 * чисел, каждая из которых закрывает свой способ обмануться:
 *
 *   - угадано против ЦЕНЫ ВХОДА. Доля угаданного сама по себе не значит ничего:
 *     кромка есть тогда, когда угадано БОЛЬШЕ, чем стоила правда;
 *   - угадано против ФИЗИКИ. Один знак «цена против страйка» уже даёт 64–65 % на
 *     первой минуте окна и 91–92 % на четвёртой. Сигнал, бьющий цену и не
 *     бьющий физику, означает, что куплена сторона, которая и так выигрывала;
 *   - перевес против ДОЛИ КРУПНЕЙШЕГО. Перевес, который целиком дал один
 *     кошелёк, — это копирование кита под другим именем.
 *
 * # Чего на панели НЕТ и не будет
 *
 * Кнопок. У подсистемы нет порта постановки ордера — не «выключен флагом», а
 * нет в зависимостях вовсе, — и кнопка по строке, за которой не может стоять
 * ордер, обещала бы действие, которого не существует.
 */
export function CohortPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel
      title="Перевес группы «плюсовых»"
      subtitle="замер закрыт нулём — идёт, панель свёрнута"
    >
      <CohortPanelBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

/**
 * Тело сводки — ОТДЕЛЬНЫЙ компонент, и это не косметика.
 *
 * Свёрнутое тело НЕ МОНТИРУЕТСЯ, и три его хука (сводка, формы счёта, лента)
 * перестают опрашивать сервер раз в пятнадцать секунд просто за то, что панель
 * на экране. Тот же приём и тот же довод, что у MispricedPanelBody.
 *
 * Экспортируется РАДИ ТЕСТА: свёрнутую панель отрисовать в статическую разметку
 * нельзя — тела у неё нет по построению, — а утверждение теста про колонку
 * таблицы, то есть про тело.
 */
export function CohortPanelBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const summary = useCohortSummary(hours, windowLen);
  const buckets = summary.data?.buckets ?? [];
  // Форма счёта — общим запросом на все журналы: границы корзин обязаны быть
  // одни, иначе гистограммы соседних панелей перестанут сравниваться.
  const shapes = useJournalShapes(hours, "cohort", windowLen);
  // Ключ формы — ступень и минута, ровно как его собирает SQL. Один и тот же
  // ключ с двух сторон провода: разойдись они, форма молча не нашла бы свою
  // строку и исчезла, а это хуже, чем её отсутствие.
  const shapeKey = (b: CohortBucket) => `${b.threshold_bp}-${b.bucket}`;

  return (
    <>
      {summary.data && (
        <p className="mb-2 text-xs text-[var(--color-muted)]">{verdict(buckets)}</p>
      )}
      {!summary.data ? (
        <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
      ) : buckets.length === 0 ? (
        <Empty>
          строк ещё не было: ставка рождается, только когда перевес группы
          кошельков, живущих в плюсе, перешагивает ступень лесенки — долю оборота
          окна. Строки прежней выборки (абсолютный порог $2000 до 2026-09-01)
          сюда не входят: они мерили другое.
        </Empty>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">ступень</th>
                  <th className="pb-2 font-normal">минута окна</th>
                  <th className="pb-2 text-right font-normal">зачётных</th>
                  <th className="pb-2 text-right font-normal">погашено</th>
                  <th className="pb-2 text-right font-normal">сведено</th>
                  <th className="pb-2 text-right font-normal">угадано</th>
                  <th className="pb-2 text-right font-normal">против цены</th>
                  <th className="pb-2 text-right font-normal">против физики</th>
                  <th className="pb-2 text-right font-normal">после добора</th>
                  <th className="pb-2 text-right font-normal">налито</th>
                  <th className="pb-2 text-right font-normal">отбор</th>
                  <th className="pb-2 text-right font-normal">счёт М</th>
                  <th className="pb-2 text-right font-normal">счёт Т</th>
                  <RateHead />
                  <th className="pb-2 text-right font-normal">форма счёта</th>
                  <th className="pb-2 text-right font-normal">перевес</th>
                  <th className="pb-2 text-right font-normal">доля кита</th>
                  <th className="pb-2 text-right font-normal">с китом</th>
                  <th className="pb-2 text-right font-normal">кошельков</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b, i) => (
                  <BucketRow
                    key={`${b.threshold_bp}-${b.bucket}`}
                    bucket={b}
                    hours={hours}
                    shape={shapeOf(shapes, shapeKey(b))}
                    // Подпись ступени печатается ОДИН раз на группу: повторённая
                    // в каждой строке, она читалась бы как четыре разные ступени
                    // там, где это одна.
                    first={i === 0 || buckets[i - 1]?.threshold_bp !== b.threshold_bp}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Приговор форме — строкой на ведро: держится ли счёт на кромке или
              на горстке сделок. Колонка «счёт» этого не говорит. */}
          <Note label="ставка и хрупкость — по группам">
            <ul className="space-y-1 text-xs text-[var(--color-muted)]">
              {buckets.map((b) => {
                const shape = shapeOf(shapes, shapeKey(b));
                return (
                  <li key={`${b.threshold_bp}-${b.bucket}`}>
                    <b>
                      {stepLabel(b.threshold_bp)}, {bucketLabel(b.bucket)}
                    </b>{" "}
                    —{" "}
                    <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                    <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>.
                  </li>
                );
              })}
            </ul>
          </Note>
          <Note label="как читать: z-оценки, доля кита и отбор">
            <b>«Против цены» и «против физики» — это z-оценки</b>, а не разности:
            кромка объявляется запасом в сигмах, потому что знак разности на такой
            выборке не значит ничего. <b>Оба сравнения обязательны.</b> Первое
            отвечает «дешевле ли мы вошли, чем стоила правда», второе — «знает ли
            группа что-то сверх знака „цена против страйка“», у которого своя планка
            на каждой минуте: 64–65 % на первой, 91–92 % на четвёртой.{" "}
            <b>«После добора» — третье сравнение и главное в этой вехе:</b> доля
            угаданного среди строк, где перевес к подтверждению ВЫРОС, против цены
            НА МОМЕНТ ПОДТВЕРЖДЕНИЯ. Вся информация о перевесе приходит после
            входа, но платят за неё ушедшей ценой, и колонка отвечает, сколько
            после этой платы осталось.{" "}
            <b>«Доля кита» — канарейка всей затеи:</b> устойчиво больше половины
            означает, что меряется один кошелёк, а не группа; соседняя колонка
            «с китом» показывает кромку именно на таких строках — с 2026-09-01 это
            разрез, а не гашение.{" "}
            <b>«Отбор» — угадано среди налитых минус угадано среди всех:</b>{" "}
            отрицательное значение означает, что мейкера наливают охотнее там, где он
            неправ. <b>«Погашено» не прячется</b> — без него нельзя ответить,
            сколько потока съели пороги. Ордеров подсистема не ставит и поставить не
            может: порта постановки у неё нет.
          </Note>
        </>
      )}
    </>
  );
}

/**
 * Лента теневых ставок: по строке на решение, вместе с погашенными.
 *
 * Отдельным компонентом от сводки, чтобы страница могла поставить ленты НИЖЕ
 * обеих сводок: сравнивают сводки друг с другом, а не со своей лентой.
 */
export function CohortFeed({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel
      title="Ставки по перевесу группы"
      subtitle={`за ${hours} ч, включая погашенные правилом`}
    >
      <CohortFeedBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

/**
 * Тело ленты — ОТДЕЛЬНЫЙ компонент, и это не косметика.
 *
 * Под FoldablePanel неразвёрнутое тело НЕ МОНТИРУЕТСЯ, а значит хук не заводит
 * опроса: свёрнутая лента молчит, а не просто прячет таблицу. Страница отвечает
 * на вопрос «есть ли кромка», и лог ставок ей для этого не нужен.
 */
function CohortFeedBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const feed = useCohortSignals(hours, undefined, windowLen);
  const signals = feed.data?.signals ?? [];

  return (
    <>
      {!feed.data ? (
        <Empty>{feed.isError ? "лента не загрузилась" : "загрузка…"}</Empty>
      ) : signals.length === 0 ? (
        <Empty>строк за период не было</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-panel)]">
              <tr className="text-left text-[var(--color-muted)]">
                <th className="pb-2 font-normal">когда</th>
                <th className="pb-2 font-normal">окно</th>
                <th className="pb-2 font-normal">исход</th>
                <th className="pb-2 text-right font-normal">ступень</th>
                <th className="pb-2 text-right font-normal">перевес</th>
                <th className="pb-2 text-right font-normal">из них кит</th>
                <th className="pb-2 text-right font-normal">кошельков</th>
                <th className="pb-2 text-right font-normal">фора</th>
                <th className="pb-2 text-right font-normal">мейкер</th>
                <th className="pb-2 text-right font-normal">тейкер</th>
                <th className="pb-2 text-right font-normal">подтв.</th>
                <th className="pb-2 text-right font-normal">физика</th>
                <th className="pb-2 font-normal">что стало</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((s) => (
                <FeedRow key={s.id} signal={s} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function BucketRow({
  bucket,
  shape,
  hours,
  first,
}: {
  bucket: CohortBucket;
  shape: ShadowShape | undefined;
  hours: number;
  first: boolean;
}) {
  const adv = adverse(bucket);
  return (
    <tr
      className={
        first
          ? "border-t-2 border-[var(--color-muted)]"
          : "border-t border-[var(--color-border)]"
      }
    >
      <td
        className="py-1"
        title={
          first
            ? `ступень считается от оборота окна; медиана оборота ${usd(
                bucket.window_gross_median,
              )}, то есть ступень ≈ ${stepUSD(bucket)}`
            : undefined
        }
      >
        {first ? stepLabel(bucket.threshold_bp) : ""}
      </td>
      <td className="py-1">{bucketLabel(bucket.bucket)}</td>
      <td className="py-1 text-right num">{bucket.signals}</td>
      <td className="py-1 text-right num text-[var(--color-muted)]">
        {bucket.suppressed}
      </td>
      <td className="py-1 text-right num">{bucket.settled}</td>
      <td className="py-1 text-right num">{ratio(bucket.won, bucket.settled)}</td>
      <td
        className="py-1 text-right num"
        style={{ color: zTone(bucket.price_z) }}
        title={`сумма цены входа ${bucket.price_sum} по ${bucket.priced} строкам`}
      >
        {zWord(bucket.price_z)}
      </td>
      <td
        className="py-1 text-right num"
        style={{ color: zTone(bucket.phys_z) }}
        title={
          bucket.phys_known === 0
            ? "замка на этих строках не наблюдали: сравнивать не с чем, и ноль здесь выдал бы незнание за измерение"
            : `сумма вероятности физики ${bucket.phys_sum} по ${bucket.phys_known} строкам`
        }
      >
        {zWord(bucket.phys_z)}
      </td>
      {/* Подтверждение — ТРЕТЬЕ сравнение, и знаменатель у него свой: цена на
          момент подтверждения. Ради него веха и делалась: вся информация о
          перевесе приходит после входа, но платят за неё ушедшей ценой, и без
          этой колонки ожидание выглядело бы бесплатным. */}
      <td
        className="py-1 text-right num"
        style={{ color: zTone(bucket.grew_z) }}
        title={
          bucket.grew === 0
            ? `перевес к подтверждению не вырос ни у одной из ${bucket.confirmed} строк: сравнивать не с чем`
            : `${bucket.grew} строк с добором из ${bucket.confirmed} подтверждённых; ` +
              `угадано ${bucket.won_grew} против суммы цены подтверждения ${bucket.grew_sum}`
        }
      >
        {zWord(bucket.grew_z)}
      </td>
      <td
        className="py-1 text-right num"
        title={`наблюдалась лестница у ${bucket.min_print_known} строк`}
      >
        {ratio(bucket.filled, bucket.min_print_known)}
      </td>
      <td
        className="py-1 text-right num"
        style={{ color: adv !== null && adv < 0 ? "var(--color-bad)" : undefined }}
        title="угадано среди налитых минус угадано среди всех, в процентных пунктах"
      >
        {adv === null ? "—" : `${adv > 0 ? "+" : "−"}${Math.abs(adv).toFixed(0)} п.п.`}
      </td>
      {/* Счёт — КОНТРОЛЬНАЯ величина, и стоит он после долей намеренно: доли
          отвечают «есть ли кромка», счёт — «сошлось ли это с деньгами». Прочерк
          при нулевом знаменателе: у ведра без налитых заявок мейкерского счёта
          не существует, и ноль читался бы как измеренный. */}
      <td
        className="py-1 text-right num"
        style={{ color: pnlTone(bucket.maker_pnl, bucket.filled) }}
        title={`по ${bucket.filled} налитым заявкам`}
      >
        {pnlWord(bucket.maker_pnl, bucket.filled)}
      </td>
      <td
        className="py-1 text-right num"
        style={{ color: pnlTone(bucket.taker_pnl, bucket.settled) }}
        title={`по ${bucket.settled} сведённым строкам`}
      >
        {pnlWord(bucket.taker_pnl, bucket.settled)}
      </td>
      {/* Удельный счёт — ВПРИТЫК к тейкерской сумме: форма счёта считается по
          ней же (shadow_taker_pnl), и ставка обязана стоять у того числа, из
          которого посчитана. */}
      <RateCell shape={shape} hours={hours} />
      {/* Форма СЧЁТА тейкера: сумма слева отвечает «сколько», гистограмма —
          «из чего». Ведро с плюсовой медианой и минусовым счётом (несколько
          крупных потерь) по колонке «счёт» неотличимо от ровного минуса. */}
      <td className="py-1 text-right">
        <PnLShape shape={shape} />
      </td>
      <td className="py-1 text-right num">{usd(bucket.tilt_median)}</td>
      <td
        className="py-1 text-right num"
        style={{ color: soloTone(bucket.top_share_median) }}
        title="медианная доля самого крупного участника в перевесе группы"
      >
        {soloShare(bucket.top_share_median)}
      </td>
      {/* Разрез по киту, а не гейт. До 2026-09-01 такие строки гасились
          правилом; замер показал, что оно съедало 294 строки из 1220, а кромка у
          погашенных была +1.3 п.п. против −2.0 п.п. у зачётных. */}
      <td
        className="py-1 text-right num"
        style={{ color: zTone(bucket.conc_z) }}
        title={
          bucket.concentrated === 0
            ? "строк, где больше половины перевеса дал один кошелёк, в этом разрезе нет"
            : `${bucket.concentrated} строк, где больше половины перевеса дал один ` +
              `кошелёк; угадано ${bucket.won_concentrated} против суммы цены ${bucket.conc_sum}`
        }
      >
        {zWord(bucket.conc_z)}
      </td>
      <td className="py-1 text-right num">{bucket.wallets_median}</td>
    </tr>
  );
}

function FeedRow({ signal }: { signal: CohortSignal }) {
  const share =
    Number(signal.tilt_usd) !== 0
      ? String(Math.abs(Number(signal.top_wallet_usd) / Number(signal.tilt_usd)))
      : null;
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1 text-[var(--color-muted)]">
        {new Date(signal.detected_at).toLocaleTimeString("ru-RU")}
      </td>
      <td className="py-1">{signal.slug ?? signal.condition_id.slice(0, 10)}</td>
      <td className="py-1">
        {signal.outcome_name || signal.asset_id.slice(0, 8)}
        {signal.reversal ? (
          <span
            className="ml-1 text-[var(--color-muted)]"
            title="вторая строка окна: группа развернулась и набрала столько же в противоположную сторону"
          >
            ↺
          </span>
        ) : null}
      </td>
      {/* Ступень и её знаменатель: доля без оборота окна не сравнима между
          окнами, а доллары без доли — между рынками. */}
      <td
        className="py-1 text-right num"
        title={`оборот окна на момент срабатывания ${usd(signal.window_gross_usd)}, ступень в деньгах ${usd(signal.enter_usd)}`}
      >
        {stepLabel(signal.threshold_bp)}
      </td>
      <td className="py-1 text-right num">{usd(signal.tilt_usd)}</td>
      <td
        className="py-1 text-right num"
        style={{ color: soloTone(share) }}
        title={`перевес крупнейшего участника ${usd(signal.top_wallet_usd)}`}
      >
        {soloShare(share)}
      </td>
      <td
        className="py-1 text-right num"
        title={`${signal.wallets_long} за · ${signal.wallets_short} против · ${signal.wallets_seen} задели окно`}
      >
        {signal.wallets_long}
      </td>
      <td className="py-1 text-right num">{lead(signal.lead_ms)}</td>
      <td
        className="py-1 text-right num"
        title={
          signal.min_print_price
            ? `минимум принта ${signal.min_print_price}: бид по цене X налили бы ⟺ минимум ≤ X`
            : "лестницу входа не наблюдали"
        }
      >
        {Number(signal.plan_price) > 0 ? signal.plan_price : "—"}
        {signal.maker_filled === true ? " ✓" : signal.maker_filled === false ? " ✗" : ""}
      </td>
      <td
        className="py-1 text-right num"
        title={
          signal.taker_price
            ? "цена тейкерского входа на момент срабатывания"
            : `цены не было: ${takerReasonWord(signal.taker_reason)}`
        }
      >
        {signal.taker_price ?? "—"}
      </td>
      {/* Точка подтверждения: цена, которой платят за ожидание, и перевес на тот
          же момент. Прочерк означает «точку не наблюдали», а НЕ «перевес не
          удержался». */}
      <td
        className="py-1 text-right num"
        title={confirmTitle(signal)}
      >
        {signal.confirm_price ?? "—"}
        {signal.confirm_tilt_usd && Number(signal.confirm_tilt_usd) > Number(signal.tilt_usd)
          ? " ↑"
          : signal.confirm_at
            ? " ↓"
            : ""}
      </td>
      <td
        className="py-1 text-right num text-[var(--color-muted)]"
        title={
          signal.p_phys
            ? `вероятность нашей стороны по замку, ревизия ${signal.model_rev ?? "?"}`
            : "замка на этот момент не наблюдали: строка в сравнение с физикой не входит"
        }
      >
        {signal.p_phys ?? "—"}
      </td>
      <td className="py-1" style={{ color: rowTone(signal) }} title={signal.rule ? ruleWord(signal.rule) : undefined}>
        {outcomeWord(signal)}
      </td>
    </tr>
  );
}
